import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { useLang } from '../context/LangContext';
import StatusBadge from '../components/StatusBadge';
import AuditeeMultiSelect from '../components/AuditeeMultiSelect';

const STATUSES   = ['niet_beoordeeld', 'conform', 'niet_conform', 'observatie', 'kans_verbetering', 'nvt'];
const PRIORITIES = ['laag', 'gemiddeld', 'hoog', 'kritiek'];
const MATURITY_LEVELS = [
  [0, 'Onvolledig'], [1, 'Initieel'], [2, 'Beheerd'],
  [3, 'Gedefinieerd'], [4, 'Kwantitatief beheerd'], [5, 'Optimaliserend'],
];
const AUTOSAVE_DELAY = 1500; // ms

export default function FindingPage() {
  const { auditId, findingId } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();

  const [finding, setFinding]   = useState(null);
  const [siblings, setSiblings] = useState([]); // alle findings van deze audit (voor nav)
  const [auditQuestions, setAuditQuestions] = useState([]);
  const [questionsOpen, setQuestionsOpen]   = useState(true);
  const [loading, setLoading]   = useState(true);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [form, setForm]         = useState({});
  const [uploading, setUploading] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const [polishLang, setPolishLang] = useState('nl');
  const [history, setHistory]         = useState(null); // null = niet geladen
  const [historyOpen, setHistoryOpen] = useState(false);

  async function toggleHistory() {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next && history === null) {
      try {
        const res = await api.get(`/api/findings/${findingId}/history`);
        setHistory(res.data);
      } catch { setHistory([]); }
    }
  }

  const [verifyResults, setVerifyResults] = useState({});
  const [verifyingId, setVerifyingId]     = useState(null);

  async function verifyEvidence(id) {
    setVerifyingId(id);
    try {
      const res = await api.get(`/api/evidence/${id}/verify`);
      setVerifyResults(v => ({ ...v, [id]: res.data }));
    } catch {
      setVerifyResults(v => ({ ...v, [id]: { error: true } }));
    } finally { setVerifyingId(null); }
  }

  // Bewijs-checklist (framework-template, read-only — beheer via Frameworks-menu)
  const [checklist, setChecklist] = useState([]);
  const [auditeeContacts, setAuditeeContacts] = useState([]);
  const [auditeeIds,      setAuditeeIds]      = useState([]);

  const formRef    = useRef(form);
  const dirtyRef   = useRef(false);
  const autosaveTimer = useRef(null);

  // Sync formRef met actuele form-state zodat timers altijd de laatste waarden zien
  useEffect(() => { formRef.current = form; }, [form]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/api/findings/${findingId}`),
      api.get(`/api/audits/${auditId}/findings`),
    ]).then(([fRes, allRes]) => {
      const f = fRes.data;
      setFinding(f);
      setSiblings(allRes.data);
      // Auditees (per bevinding; leeg = erft de algemene audit-auditees)
      setAuditeeIds((f.auditees || []).map(a => a.id));
      if (f.client_id) {
        api.get(`/api/clients/${f.client_id}/contacts`).then(r => setAuditeeContacts(r.data)).catch(() => {});
      }
      // Checklist ophalen zodra we de control_db_id kennen
      if (f.control_db_id) {
        api.get(`/api/controls/${f.control_db_id}/checklist`)
          .then(r => setChecklist(r.data))
          .catch(() => setChecklist([]));
      }
      // Auditvragen: opgeslagen als JSON per finding (control-specifiek)
      const aq = f.audit_questions;
      if (aq) {
        try { setAuditQuestions(typeof aq === 'string' ? JSON.parse(aq) : aq); }
        catch { setAuditQuestions([]); }
      } else {
        setAuditQuestions([]);
      }
      setForm({
        status:            f.status,
        notes:             f.notes             || '',
        notes_visible:     !f.notes_private, // false = privé van een andere gebruiker
        current_situation: f.current_situation || '',
        finding:           f.finding           || '',
        recommendation:    f.recommendation    || '',
        priority:          f.priority          || '',
        due_date:          f.due_date ? f.due_date.slice(0, 10) : '',
        evidence_notes:    f.evidence_notes    || '',
        maturity_level:    f.maturity_level ?? null,
      });
      dirtyRef.current = false;
      setSaveState('idle');
    }).finally(() => setLoading(false));
  }, [findingId, auditId]);

  useEffect(load, [load]);

  function saveFindingAuditees(ids) {
    setAuditeeIds(ids);
    api.put(`/api/findings/${findingId}/auditees`, { contact_ids: ids }).catch(() => {});
  }

  // Cleanup timer bij unmount of bij navigeren
  useEffect(() => () => {
    clearTimeout(autosaveTimer.current);
  }, [findingId]);

  function set(field, val) {
    setForm(f => ({ ...f, [field]: val }));
    dirtyRef.current = true;
    setSaveState('idle');

    // Debounce autosave
    clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => doSave(), AUTOSAVE_DELAY);
  }

  async function doSave(formOverride) {
    const data = formOverride || formRef.current;
    // notes_visible is een interne UI-vlag, niet voor de API. En zijn de
    // notities privé van een andere gebruiker (niet zichtbaar), dan sturen we
    // het veld bewust NIET mee, zodat we andermans notitie niet overschrijven.
    const { notes_visible, ...payload } = data;
    if (notes_visible === false) delete payload.notes;
    setSaveState('saving');
    try {
      await api.put(`/api/findings/${findingId}`, payload);
      dirtyRef.current = false;
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 2000);
    } catch {
      setSaveState('error');
    }
  }

  async function handleSave(e) {
    e?.preventDefault();
    clearTimeout(autosaveTimer.current);
    await doSave();
  }

  // Navigeer naar vorige/volgende — sla eerst op als er wijzigingen zijn
  async function handleNavigate(targetId) {
    clearTimeout(autosaveTimer.current);
    if (dirtyRef.current) await doSave();
    navigate(`/audits/${auditId}/findings/${targetId}`);
  }

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/api/findings/${findingId}/evidence`, fd);
      load();
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  async function handleDeleteEvidence(evidenceId) {
    if (!window.confirm(t('confirm_delete'))) return;
    await api.delete(`/api/evidence/${evidenceId}`);
    load();
  }

  // Bewijsstuk geauthenticeerd ophalen en openen (niet meer via publieke URL)
  async function openEvidence(ev) {
    try {
      const res = await api.get(`/api/evidence/${ev.id}/file`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch {
      window.alert('Kon bewijsstuk niet openen');
    }
  }

  async function handlePolish() {
    // Eerst opslaan zodat de laatste notities beschikbaar zijn
    clearTimeout(autosaveTimer.current);
    if (dirtyRef.current) await doSave();

    setPolishing(true);
    try {
      const res = await api.post(`/api/audits/${auditId}/polish-one`, {
        language: polishLang,
        finding_id: Number(findingId),
      });
      const result = res.data?.results?.[0];
      if (result) {
        setForm(f => ({
          ...f,
          current_situation: result.current_situation || f.current_situation,
          finding:           result.finding           || f.finding,
          recommendation:    result.recommendation    || f.recommendation,
        }));
        // Polished_at bijwerken
        setFinding(prev => ({ ...prev, polished_at: new Date().toISOString(), polished_lang: polishLang }));
        dirtyRef.current = false;
        setSaveState('saved');
        setTimeout(() => setSaveState('idle'), 2000);
      }
    } catch (err) {
      const msg = err?.response?.data?.error || t('error');
      alert(msg);
    } finally {
      setPolishing(false);
    }
  }

  // Vorige / volgende bepalen
  const currentIdx = siblings.findIndex(f => String(f.id) === String(findingId));
  const prevFinding = currentIdx > 0 ? siblings[currentIdx - 1] : null;
  const nextFinding = currentIdx >= 0 && currentIdx < siblings.length - 1 ? siblings[currentIdx + 1] : null;

  if (loading) return <div className="page-body"><div className="spinner-border spinner-border-sm" /></div>;
  if (!finding) return <div className="page-body text-danger">{t('error')}</div>;

  const saveIndicator = {
    idle:   null,
    saving: <span className="text-muted small"><span className="spinner-border spinner-border-sm me-1" style={{width:12,height:12}} />Opslaan…</span>,
    saved:  <span className="text-success small"><i className="bi bi-check2 me-1" />Opgeslagen</span>,
    error:  <span className="text-danger small"><i className="bi bi-exclamation-triangle me-1" />Fout bij opslaan</span>,
  }[saveState];

  return (
    <>
      <div className="page-header">
        <div>
          <div className="text-muted-sm mb-1">
            <Link to="/audits" className="text-muted text-decoration-none">{t('nav_audits')}</Link>
            {' / '}
            <Link to={`/audits/${auditId}`} className="text-muted text-decoration-none">{t('findings_title')}</Link>
            {' / '}
          </div>
          <h2 className="mb-0">{finding.control_id} — {finding.title}</h2>
        </div>

        <div className="d-flex gap-2 align-items-center">
          {saveIndicator}

          {/* Navigatie */}
          <div className="btn-group">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => handleNavigate(prevFinding.id)}
              disabled={!prevFinding}
              title={prevFinding ? `${prevFinding.control_id} – ${prevFinding.title}` : ''}
            >
              <i className="bi bi-chevron-left" />
            </button>
            <span className="btn btn-outline-secondary btn-sm disabled" style={{ cursor: 'default', minWidth: 70, textAlign: 'center' }}>
              {currentIdx + 1} / {siblings.length}
            </span>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => handleNavigate(nextFinding.id)}
              disabled={!nextFinding}
              title={nextFinding ? `${nextFinding.control_id} – ${nextFinding.title}` : ''}
            >
              <i className="bi bi-chevron-right" />
            </button>
          </div>

          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saveState === 'saving'}>
            {t('finding_save')}
          </button>
        </div>
      </div>

      {/* Prev/next labels */}
      {(prevFinding || nextFinding) && (
        <div className="d-flex justify-content-between px-3 py-1" style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: 12, color: '#64748b' }}>
          <span>
            {prevFinding
              ? <button className="btn btn-link btn-sm p-0 text-muted text-decoration-none" style={{fontSize:12}} onClick={() => handleNavigate(prevFinding.id)}>
                  <i className="bi bi-chevron-left me-1" />{prevFinding.control_id} {prevFinding.title?.slice(0, 50)}
                </button>
              : <span />}
          </span>
          <span>
            {nextFinding
              ? <button className="btn btn-link btn-sm p-0 text-muted text-decoration-none" style={{fontSize:12}} onClick={() => handleNavigate(nextFinding.id)}>
                  {nextFinding.control_id} {nextFinding.title?.slice(0, 50)}<i className="bi bi-chevron-right ms-1" />
                </button>
              : <span />}
          </span>
        </div>
      )}

      <div className="page-body">
        <form onSubmit={handleSave}>
          <div className="row g-3 align-items-start">

            {/* ── Linkerkolom: schrijfwerk ───────────────────── */}
            <div className="col-12 col-lg-7">

              {/* Notities — privé voor eigenaar/admin */}
              <div className="card mb-3">
                <div className="card-header fw-semibold small d-flex align-items-center gap-2">
                  {t('finding_notes')}
                  <span className="badge bg-secondary fw-normal" style={{ fontSize: 10 }}>
                    <i className="bi bi-lock me-1" />Privé — niet in rapport
                  </span>
                </div>
                <div className="card-body">
                  {form.notes_visible === false ? (
                    <div className="text-muted small fst-italic">
                      <i className="bi bi-lock me-1" />Notities zijn ingevoerd door een andere gebruiker en niet zichtbaar.
                    </div>
                  ) : (
                    <textarea
                      className="form-control"
                      value={form.notes}
                      onChange={e => set('notes', e.target.value)}
                      rows={5}
                      placeholder={t('finding_notes_hint')}
                    />
                  )}
                </div>
              </div>

              {/* Uitgeschreven velden */}
              <div className="card mb-3">
                <div className="card-header fw-semibold small d-flex justify-content-between align-items-center">
                  <span>Rapportvelden</span>
                  <div className="d-flex gap-2 align-items-center">
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 'auto', fontSize: 12 }}
                      value={polishLang}
                      onChange={e => setPolishLang(e.target.value)}
                      disabled={polishing}
                    >
                      <option value="nl">NL</option>
                      <option value="en">EN</option>
                    </select>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={handlePolish}
                      disabled={polishing || !form.notes}
                      title={!form.notes ? 'Voeg eerst notities toe' : 'AI polish voor dit control'}
                    >
                      {polishing
                        ? <><span className="spinner-border spinner-border-sm me-1" style={{width:12,height:12}} />Bezig…</>
                        : <>✨ Polish</>}
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label small fw-medium">{t('finding_situation')}</label>
                    <textarea className="form-control" value={form.current_situation}
                      onChange={e => set('current_situation', e.target.value)} rows={4} />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-medium">{t('finding_finding')}</label>
                    <textarea className="form-control" value={form.finding}
                      onChange={e => set('finding', e.target.value)} rows={5} />
                  </div>
                  <div>
                    <label className="form-label small fw-medium">{t('finding_recommendation')}</label>
                    <textarea className="form-control" value={form.recommendation}
                      onChange={e => set('recommendation', e.target.value)} rows={4} />
                  </div>
                </div>
              </div>
            </div>

            {/* ── Rechterkolom: context + metadata ──────────── */}
            <div className="col-12 col-lg-5">

              {/* Control info */}
              <div className="card mb-3">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-1">
                    <div className="text-muted small font-mono">{finding.section} › {finding.control_id}</div>
                    <StatusBadge status={form.status} />
                  </div>
                  <div className="small">{finding.control_description}</div>
                  {finding.polished_at && (
                    <div className="text-muted-sm mt-2">
                      ✨ {t('finding_polished')}: {new Date(finding.polished_at).toLocaleString()} ({finding.polished_lang?.toUpperCase()})
                    </div>
                  )}
                </div>
              </div>

              {/* Status */}
              <div className="card mb-3">
                <div className="card-header fw-semibold small">{t('audit_status')}</div>
                <div className="card-body">
                  <div className="d-flex flex-wrap gap-2">
                    {STATUSES.map(s => (
                      <button
                        key={s}
                        type="button"
                        className={`status-option ${form.status === s ? `selected-${s}` : ''}`}
                        onClick={() => set('status', s)}
                      >
                        {t(`fs_${s}`)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Auditvragen */}
              {auditQuestions.length > 0 && (
                <div className="card mb-3">
                  <div
                    className="card-header fw-semibold small d-flex justify-content-between align-items-center"
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => setQuestionsOpen(o => !o)}
                  >
                    <span>
                      <i className="bi bi-question-circle me-2 text-primary" />
                      Auditvragen ({auditQuestions.length})
                    </span>
                    <i className={`bi bi-chevron-${questionsOpen ? 'up' : 'down'}`} style={{ fontSize: 11 }} />
                  </div>
                  {questionsOpen && (
                    <div className="card-body p-0">
                      <ol className="mb-0" style={{ paddingLeft: 32, paddingTop: 12, paddingBottom: 12 }}>
                        {auditQuestions.map((q, i) => (
                          <li key={i} className="mb-2 small" style={{ paddingRight: 16 }}>{q}</li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              )}

              {/* Bewijs-checklist (framework-template, read-only) — hoog geplaatst */}
              {checklist.length > 0 && (
                <div className="card mb-3">
                  <div className="card-header fw-semibold small d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-card-checklist text-primary" />
                      Te vragen documenten
                    </div>
                    <Link to="/frameworks" className="text-muted small text-decoration-none" style={{ fontSize: 11 }}>
                      <i className="bi bi-pencil me-1" />Beheren
                    </Link>
                  </div>
                  <div className="card-body p-2">
                    <ul className="list-unstyled mb-0">
                      {checklist.map(item => (
                        <li key={item.id} className="d-flex align-items-center gap-2 py-1 px-1"
                          style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <i className="bi bi-file-earmark-text text-muted flex-shrink-0" style={{ fontSize: 13 }} />
                          <span className="small">{item.label}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Prioriteit + Streefdatum */}
              <div className="card mb-3">
                <div className="card-header fw-semibold small">Planning</div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-6">
                      <label className="form-label small fw-medium">{t('finding_priority')}</label>
                      <select className="form-select form-select-sm" value={form.priority}
                        onChange={e => set('priority', e.target.value)}>
                        <option value="">—</option>
                        {PRIORITIES.map(p => <option key={p} value={p}>{t(`prio_${p}`)}</option>)}
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label small fw-medium">{t('finding_due_date')}</label>
                      <input type="date" className="form-control form-control-sm" value={form.due_date}
                        onChange={e => set('due_date', e.target.value)} />
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="form-label small fw-medium">{t('auditees')}</label>
                    <div>
                      <AuditeeMultiSelect
                        contacts={auditeeContacts}
                        value={auditeeIds}
                        onChange={saveFindingAuditees}
                        placeholder={t('auditees_inherit')}
                        idPrefix="finding-auditee"
                      />
                    </div>
                    <div className="form-text">{t('auditees_finding_hint')}</div>
                  </div>
                  {finding.framework_code === 'iso27001' && /^A\./.test(finding.section || '') && (
                    <div className="mt-3">
                      <label className="form-label small fw-medium">{t('maturity')}</label>
                      <select className="form-select form-select-sm"
                        value={form.maturity_level ?? ''}
                        onChange={e => set('maturity_level', e.target.value === '' ? null : Number(e.target.value))}>
                        <option value="">{t('maturity_na')}</option>
                        {MATURITY_LEVELS.map(([n, label]) => (
                          <option key={n} value={n}>{n} — {label}</option>
                        ))}
                      </select>
                      <div className="form-text">{t('maturity_hint')}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Bewijs */}
              <div className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center fw-semibold small">
                  <span>{t('finding_evidence')}</span>
                  <label className="btn btn-sm btn-outline-secondary mb-0" style={{ cursor: 'pointer' }}>
                    {uploading
                      ? <span className="spinner-border spinner-border-sm" />
                      : <><i className="bi bi-upload me-1" />Bestand</>}
                    <input type="file" onChange={handleUpload} className="d-none" disabled={uploading} multiple />
                  </label>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label small fw-medium text-muted">
                      <i className="bi bi-pencil me-1" />Tekstueel bewijs / referenties
                    </label>
                    <textarea
                      className="form-control form-control-sm"
                      rows={3}
                      placeholder="bv. Beleidsdocument v2.3, zie SharePoint /Beleid/IB"
                      value={form.evidence_notes}
                      onChange={e => set('evidence_notes', e.target.value)}
                    />
                  </div>
                  {finding.evidence?.length > 0 && (
                    <div className="d-flex flex-column gap-2">
                      {finding.evidence.map(ev => (
                        <div key={ev.id} className="evidence-item">
                          <button type="button"
                            className="btn btn-link p-0 text-start text-decoration-none"
                            onClick={() => openEvidence(ev)}>
                            <i className="bi bi-paperclip me-1" />{ev.original_name}
                          </button>
                          <div className="d-flex gap-2 align-items-center">
                            {(() => {
                              const vr = verifyResults[ev.id];
                              if (vr && vr.recorded && vr.match) return <span className="badge badge-conform" title={t('ev_verify_ok')}><i className="bi bi-shield-check" /></span>;
                              if (vr && vr.recorded && !vr.match) return <span className="badge bg-danger" title={t('ev_verify_fail')}><i className="bi bi-shield-exclamation" /></span>;
                              if (vr && vr.recorded === false && !vr.error) return <span className="text-muted small" title={t('ev_verify_none')}>—</span>;
                              if (vr && vr.error) return (
                                <button type="button" className="btn btn-sm btn-outline-warning"
                                  onClick={() => verifyEvidence(ev.id)} disabled={verifyingId === ev.id} title={t('ev_verify_error')}>
                                  {verifyingId === ev.id
                                    ? <span className="spinner-border spinner-border-sm" style={{ width: 11, height: 11 }} />
                                    : <i className="bi bi-shield-exclamation" />}
                                </button>
                              );
                              return (
                                <button type="button" className="btn btn-sm btn-outline-secondary"
                                  onClick={() => verifyEvidence(ev.id)} disabled={verifyingId === ev.id} title={t('ev_verify')}>
                                  {verifyingId === ev.id
                                    ? <span className="spinner-border spinner-border-sm" style={{ width: 11, height: 11 }} />
                                    : <i className="bi bi-shield" />}
                                </button>
                              );
                            })()}
                            <span className="text-muted small">{ev.uploaded_by_name}</span>
                            <button type="button" className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteEvidence(ev.id)}>
                              <i className="bi bi-trash" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>{/* card-body bewijs */}
              </div>{/* card bewijs */}

              {/* Geschiedenis */}
              <div className="card mb-3">
                <div className="card-header fw-semibold small d-flex justify-content-between align-items-center"
                  style={{ cursor: 'pointer', userSelect: 'none' }} onClick={toggleHistory}>
                  <span><i className="bi bi-clock-history me-2 text-primary" />{t('hist_title')}</span>
                  <i className={`bi bi-chevron-${historyOpen ? 'up' : 'down'}`} style={{ fontSize: 11 }} />
                </div>
                {historyOpen && (
                  <div className="card-body p-2">
                    {history === null ? (
                      <div className="text-muted small text-center py-2"><span className="spinner-border spinner-border-sm me-2" />{t('loading')}</div>
                    ) : history.length === 0 ? (
                      <div className="text-muted small text-center py-2">{t('hist_none')}</div>
                    ) : (
                      <ul className="list-unstyled mb-0 small">
                        {history.map((h, i) => (
                          <li key={i} className="py-1" style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <div className="fw-medium">{t(`hist_f_${h.field}`)}</div>
                            <div className="text-muted" style={{ fontSize: 12 }}>
                              {h.changed_by_name || '—'} · {new Date(h.changed_at).toLocaleString()}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>{/* col rechterkolom */}
          </div>{/* row */}

          {/* Navigatieknoppen onderaan */}
          <div className="d-flex justify-content-between gap-2 mt-1">
            <Link to={`/audits/${auditId}`} className="btn btn-outline-secondary">
              <i className="bi bi-list-ul me-1" />{t('findings_title')}
            </Link>
            <div className="d-flex gap-2">
              {prevFinding && (
                <button type="button" className="btn btn-outline-secondary btn-sm"
                  onClick={() => handleNavigate(prevFinding.id)}>
                  <i className="bi bi-chevron-left me-1" />{prevFinding.control_id}
                </button>
              )}
              {nextFinding && (
                <button type="button" className="btn btn-primary btn-sm"
                  onClick={() => handleNavigate(nextFinding.id)}>
                  {nextFinding.control_id}<i className="bi bi-chevron-right ms-1" />
                </button>
              )}
            </div>
          </div>

        </form>
      </div>

      {/* Polish-modal — toont dat de AI bezig is met dit control */}
      {polishing && (
        <>
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content">
                <div className="modal-body text-center py-4">
                  <div className="spinner-border text-primary mb-3" />
                  <div className="fw-medium">{t('polish_one_running')}</div>
                  <div className="text-muted small mt-1">{finding?.control_id} — {finding?.title}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" />
        </>
      )}
    </>
  );
}
